import React from "react";
import { Link } from "react-router-dom";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowOutward from "@mui/icons-material/ArrowOutward";
import { SiteShell, NextChapter } from "../components/BrandPrimitives";
import { PAGE_META } from "../utils/shareMeta";
import company from "../data/company-about.json";
import AboutSectionNavigation from "../components/AboutSectionNavigation";
import "../styles/brand-editorial.css";
import "../styles/brand-about.css";

const sections = [
  ["our-thesis", "Our thesis"],
  ["what-we-do", "What we do"],
  ["our-approach", "Our approach"],
  ["who-we-build-for", "Who we build for"],
  ["our-team", "Our story & team"],
  ["how-it-works", "How it works"],
  ["key-facts", "Key facts"],
  ["questions", "Questions"],
];

function Section({ id, number, title, children }) {
  return (
    <section className="brand-about-section" id={id} aria-labelledby={id + "-title"}>
      <div className="brand-about-section-head">
        <span className="brand-eyebrow">{number}</span>
        <h2 id={id + "-title"}>{title}</h2>
      </div>
      <div className="brand-about-section-body">{children}</div>
    </section>
  );
}

function ThesisSection({ id, title, className = "", children }) {
  return (
    <section id={id} className={`brand-about-thesis-section ${className}`} aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`}>{title}</h3>
      <div className="brand-about-thesis-section-copy">{children}</div>
    </section>
  );
}

function ThesisCitation({ number }) {
  const source = company.thesis.evidence.notes.find(note => note.number === number);
  return <a className="brand-about-thesis-citation" href={source.href} target="_blank" rel="noreferrer" aria-label={source.source}>[{Number(number)}]</a>;
}

export default function BrandAboutPage() {
  return (
    <SiteShell {...PAGE_META.about} className="brand-about">
      <section id="about-top" className="brand-editorial-hero brand-container brand-about-hero">
        <span className="brand-eyebrow">The Lab / About GrupaAI</span>
        <h1>Building for <strong>trillions</strong><br /><span>of digital workers.</span></h1>
        <p className="brand-about-intro">{company.description}</p>
        <p className="brand-about-capabilities"><span>Multi-screen.</span>{" "}<span>Multi-device.</span>{" "}<span>Multi-application.</span>{" "}<span>Multitasking.</span></p>
        <div className="brand-about-hero-links">
          <Link className="brand-text-link brand-about-vision-link" to="/vision"><strong>Read our vision</strong> <ArrowForward fontSize="small" /></Link>
          <Link className="brand-text-link" to="/manifesto">Our principles <ArrowForward fontSize="small" /></Link>
        </div>
      </section>

      <div className="brand-container brand-about-reading">
        <AboutSectionNavigation sections={sections} />
        <div className="brand-about-content">

        <Section id="our-thesis" number="01 / Our thesis" title={company.thesis.title}>
          <article className="brand-about-thesis-essay">
            <div className="brand-about-thesis-introduction">
              <p className="brand-about-thesis-lead">{company.thesis.lead} <strong>{company.thesis.leadEmphasis}</strong> {company.thesis.leadEnd}</p>
              <p>{company.thesis.body}</p>
            </div>

            <ThesisSection id="thesis-history" title="What software left to people">
              <p>{company.thesis.history}</p>
              <p>{company.thesis.contrarian}</p>
            </ThesisSection>

            <ThesisSection id="thesis-parallels" title="When a capability becomes infrastructure">
              <p>{company.thesis.historicalParallel.production} <ThesisCitation number="01" /></p>
              <p>{company.thesis.historicalParallel.cloud} <ThesisCitation number="02" /></p>
              <p>{company.thesis.historicalParallel.implication}</p>
            </ThesisSection>

            <ThesisSection id="thesis-timing" title="Why this becomes possible now">
              <p>{company.thesis.timing.capability} <ThesisCitation number="03" /></p>
              <p>{company.thesis.timing.cost} <ThesisCitation number="04" /></p>
              <p>{company.thesis.timing.consequence}</p>
            </ThesisSection>

            <ThesisSection id="thesis-deployment" title={company.thesis.scale.title}>
              <p>{company.thesis.scale.opportunity}</p>
              <p>{company.thesis.scale.economics}</p>
              <p>{company.thesis.scale.ambition}</p>
            </ThesisSection>

            <ThesisSection id="thesis-modes" title="One Worker, many environments" className="brand-about-thesis-modes">
              <p>A Worker takes the shape of the responsibility it is given. These are overlapping ways to deploy the same foundation, from a phone or desktop to business systems and the cloud.</p>
              <dl>
                {company.thesis.surfaces.map(surface => (
                  <div key={surface.number}>
                    <dt>{surface.title}</dt>
                    <dd>{surface.text}</dd>
                  </div>
                ))}
              </dl>
              <p>{company.thesis.handoff}</p>
            </ThesisSection>

            <ThesisSection id="thesis-governance" title="How a Worker becomes infrastructure">
              <p className="brand-about-thesis-governance-statement">{company.thesis.scale.infrastructure}</p>
              <p>{company.thesis.scale.continuation}</p>
              <p>{company.thesis.demand}</p>
              <p>{company.thesis.construction}</p>
            </ThesisSection>

            <ThesisSection id="thesis-advantage" title="The advantage has to compound">
              <p>{company.thesis.advantage.body}</p>
              <p>{company.thesis.advantage.test}</p>
            </ThesisSection>

            <ThesisSection id="thesis-scale" title="From personal teams to business swarms">
              <p>{company.thesis.scale.body}</p>
              <p className="brand-about-thesis-source-note">{company.thesis.scale.note} <ThesisCitation number="06" /></p>
            </ThesisSection>

            <ThesisSection id="thesis-closing" title={company.thesis.closing.title}>
              <p>{company.thesis.closing.body}</p>
              <p className="brand-about-thesis-final"><strong>{company.thesis.closing.last}</strong></p>
            </ThesisSection>

            <Link className="brand-text-link brand-about-thesis-link brand-about-vision-link" to="/vision">
              <strong>Read our vision</strong><span>Why we are building this</span><ArrowForward fontSize="small" />
            </Link>

            <ThesisSection id="thesis-external-references" title="External references" className="brand-about-thesis-reference-list">
              <p>Published sources that inform our thesis. They provide historical, technical, and economic context; they are not GrupaAI measurements.</p>
              <ol>
                {company.thesis.evidence.notes.map(note => (
                  <li key={note.number}>
                    <h4>{Number(note.number)}. {note.title}</h4>
                    <p>{note.text}</p>
                    <a href={note.href} target="_blank" rel="noreferrer">{note.source} <ArrowOutward fontSize="small" /></a>
                  </li>
                ))}
              </ol>
            </ThesisSection>

            <ThesisSection id="thesis-lab-research" title="From the Lab">
              <p>The GrupaAI Pioneer AI Lab Journal shares our research questions, design convictions, and what we are learning as we build. These essays describe work in progress. They are not independent validation of our performance.</p>
              <Link className="brand-about-thesis-journal-link" to="/blog">Read the Journal <ArrowForward fontSize="small" /></Link>
            </ThesisSection>
          </article>
        </Section>

        <Section id="what-we-do" number="02 / The work" title="What GrupaAI does">
          <div className="brand-about-products">
            {company.products.map(product => (
              <div key={product.name}>
                <span className="brand-caption">{product.label}</span>
                <h3><Link to={product.href}>{product.name} <ArrowOutward fontSize="small" /></Link></h3>
                <p>{product.description}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="our-approach" number="03 / The approach" title="What makes GrupaAI different">
          <div className="brand-about-rows">
            {company.differences.map(item => <div key={item.title}><h3>{item.title}</h3><p>{item.text}</p></div>)}
          </div>
          <p className="brand-about-note">Explore the thinking in our <Link to="/blog">Journal</Link> and the execution model in the <Link to="/documentation">Workers API documentation</Link>.</p>
        </Section>

        <Section id="who-we-build-for" number="04 / The people" title="Who uses GrupaAI">
          <p className="brand-about-section-intro">We are building the foundation for the next generation of companies: autonomous businesses and organizations powered by digital workers. GrupaAI serves the founders creating them, the developers building their systems, and the enterprises automating their operations.</p>
          <ul className="brand-about-audiences">
            {company.audiences.map(item => <li key={item.title}><h3>{item.title}</h3><p>{item.text}</p></li>)}
          </ul>
        </Section>

        <Section id="our-team" number="05 / The people behind it" title="The team behind GrupaAI">
          <div className="brand-about-team">
            {/* Temporarily hide Kapil's card; remove this filter to restore his retained profile. */}
            {company.team.filter(person => person.id !== "kapil-bodkhe").map(person => (
              <article key={person.id} id={person.id}>
                <img className="brand-about-portrait" src={person.image} alt={person.name} width="400" height="400" loading="lazy" decoding="async" />
                <span className="brand-caption">{person.role}</span>
                <h3>{person.name}</h3>
                <p>{person.text}</p>
                {person.href && <a className="brand-text-link" href={person.href} target="_blank" rel="noreferrer">{person.linkLabel} <ArrowOutward fontSize="small" /></a>}
              </article>
            ))}
          </div>

          <div id="our-story" className="brand-about-origin" aria-labelledby="our-story-title">
            <span className="brand-eyebrow">Our origin</span>
            <h3 id="our-story-title">Software scaled.<br />The operations still needed people.</h3>
            <div className="brand-about-story">
              {company.origin.map((item, index) => (
                <div key={item.title}>
                  <span className="brand-about-story-number" aria-hidden="true">0{index + 1}</span>
                  <div><h4>{item.title}</h4><p>{item.text}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="brand-about-ambition">
            <span className="brand-eyebrow">Why a Pioneer AI Lab</span>
            <h3>Infrastructure for trillions of autonomous digital workers.</h3>
            <p>Our ambition is to make provisioning digital workers as straightforward as provisioning cloud compute. Organizations should be able to deploy and coordinate trillions of workers across their software and devices, with reliable execution, security, and human oversight built into the infrastructure.</p>
            <p>The standard we are pursuing is reliable, secure, 24/7 work: 10× human performance on defined tasks, higher reliability, and one-tenth the total cost of comparable skilled labor in the world&apos;s lowest-cost regions. These are research goals, to be evaluated against completed work, its quality, and the full cost of delivery.</p>
            <Link className="brand-text-link" to="/vision">The path from one worker to autonomous organizations <ArrowForward fontSize="small" /></Link>
          </div>

          <div className="brand-about-recruiting">
            <span className="brand-eyebrow">The team we are building</span>
            <h3>Help define the next era of work.</h3>
            <p>We are recruiting exceptional people in AI, agents, engineering, research, and security. Our ambition is to build the world&apos;s best organization for digital humanoid workers: a lab whose research breakthroughs and engineering discipline make reliable, secure work possible at the scale of trillions of workers.</p>
            <Link className="brand-text-link" to="/careers">Explore the roles and our interview process <ArrowForward fontSize="small" /></Link>
          </div>
        </Section>

        <Section id="how-it-works" number="06 / Getting started" title="How GrupaAI works">
          <ol className="brand-about-process">
            {company.process.map(item => <li key={item.title}><h3>{item.title}</h3><p>{item.text}</p></li>)}
          </ol>
          <p className="brand-about-note">For product and deployment questions, email <a href="mailto:team@grupa.ai">team@grupa.ai</a> or <a href="https://calendar.app.google/CE8yHXidRV2hsSRS9" target="_blank" rel="noreferrer">book a conversation</a>. Support arrangements and onboarding depend on the scope of the work.</p>
        </Section>

        <Section id="key-facts" number="07 / At a glance" title="Key facts">
          <dl className="brand-about-facts">
            {company.facts.map(fact => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.href ? fact.href.startsWith("/") ? <Link to={fact.href}>{fact.value}</Link> : <a href={fact.href}>{fact.value}</a> : fact.value}</dd></div>)}
          </dl>
        </Section>

        <Section id="questions" number="08 / Questions" title="Frequently asked questions">
          <div className="brand-about-faq">
            {company.faq.map(item => (
              <div key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </div>
            ))}
          </div>
        </Section>
        </div>
      </div>
      <div id="about-contact"><NextChapter title="A company worth building." text="Bring us the work you want to make possible." to="mailto:team@grupa.ai" label="Talk to us" secondaryLink={{ to: "/careers", label: "Build with us" }} /></div>
    </SiteShell>
  );
}
